import React, { useState, useEffect, useRef } from 'react';
import { schoolAPI } from '../api';

export default function Header({ schoolName, onSchoolNameChange }) {
  const [inputValue, setInputValue] = useState(schoolName || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setInputValue(schoolName || '');
  }, [schoolName]);

  const handleChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    onSchoolNameChange && onSchoolNameChange(val);
  };

  const handleSave = async () => {
    if (!inputValue.trim()) return;
    setSaving(true);
    try {
      await schoolAPI.save(inputValue.trim());
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      console.error('Failed to save school name:', e);
    } finally {
      setSaving(false);
    }
  };

  return (
    <header className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-50">
      <div className="max-w-screen-2xl mx-auto px-4 py-3 flex items-center justify-between gap-4 flex-wrap">
        {/* Branding */}
        <div className="flex items-center gap-3 shrink-0">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-blue-600 flex items-center justify-center shadow-md">
            <span className="text-white font-bold text-lg leading-none">MS</span>
          </div>
          <div>
            <h1 className="text-xl font-extrabold leading-tight bg-gradient-to-r from-orange-500 to-blue-600 bg-clip-text text-transparent">
              MS - Marks Sender
            </h1>
            <p className="text-[11px] text-gray-400 leading-none font-medium tracking-wide uppercase">
              Student Marks Management
            </p>
          </div>
        </div>

        {/* School Name Input */}
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <input
              type="text"
              value={inputValue}
              onChange={handleChange}
              placeholder="Enter your school name..."
              className="w-full pl-9 pr-10 py-2.5 rounded-lg border-2 border-blue-200 focus:border-blue-500 focus:outline-none text-sm font-medium text-gray-700 bg-blue-50 focus:bg-white transition-colors"
            />
            {saved && (
              <div className="absolute inset-y-0 right-3 flex items-center">
                <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 px-4 py-2.5 rounded-lg shadow-[0_4px_0_#1d4ed8] active:shadow-[0_2px_0_#1d4ed8] active:translate-y-0.5 transform transition-all disabled:opacity-60"
          >
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
            </svg>
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>

        {/* Status */}
        <div className="hidden md:flex items-center gap-2 text-xs text-gray-500 shrink-0">
          <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse"></div>
          <span>System Online</span>
        </div>
      </div>
    </header>
  );
}
